import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import type { ContactDetails } from "@/hooks/useContactDetails";
import { Loader2, PlusCircle } from "lucide-react";
import { format } from "date-fns";
import { useTeam } from "@/hooks/useTeam";
import type { CustomField } from "@/hooks/useCustomFields";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import { formatDateInAccountTz, formatDateTimeInAccountTz, formatTimeInAccountTz } from '@/lib/accountTimezone'

interface ContactNameProps {
  contact: ContactDetails;
  onUpdate: (updates: Partial<ContactDetails>) => Promise<boolean>;
  customFieldDefs: CustomField[];
  customFieldsLoading?: boolean;
}

const cardClass =
  "w-full p-4 flex rounded-xl flex-col bg-white border border-[#E5E7EB] shadow-sm transition-colors duration-200 dark:bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none";

const labelClass = "text-xs font-medium text-[#64748B] uppercase tracking-wide dark:text-[#A1A1AA]";
const valueClass = "text-sm font-medium text-[#334155] dark:text-[#FAFAFA]";
const inputClass =
  "h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]";

const ContactName: React.FC<ContactNameProps> = ({
  contact,
  onUpdate,
  customFieldDefs,
  customFieldsLoading = false,
}) => {
  const { t } = useTranslation("crm");
  const { members } = useTeam();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editCustomFields, setEditCustomFields] = useState<Record<string, any>>(
    (contact.custom_fields as Record<string, any>) || {}
  );

  const syncEditForm = () => {
    setEditCustomFields((contact.custom_fields as Record<string, any>) || {});
  };

  const openEditDialog = () => {
    syncEditForm();
    setEditDialogOpen(true);
  };

  const activeCustomFields = customFieldDefs.filter((f) => f.is_active);

  const populatedCustomFields = Object.entries(
    (contact.custom_fields as Record<string, any>) || {}
  ).filter(([, value]) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  });

  const attributedMember = contact.attribution_id
    ? members.find(m => m.auth_user_id === contact.attribution_id)
    : null;

  const getInitials = (name: string | null, email: string) => {
    if (name) {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return email.slice(0, 2).toUpperCase();
  };

  return (
    <div className="w-full flex flex-col gap-3 order-1">
      {/* Contact name ---------------------------> */}
      <div className={cardClass}>
        <div className="w-full flex flex-col justify-center items-center">
          {contact.profile_picture ? (
            <img
              src={contact.profile_picture}
              alt={contact.name}
              className="w-16 h-16 rounded-full object-cover"
            />
          ) : (
            <div className="w-16 h-16 bg-[#EEF4FF] rounded-full flex items-center justify-center text-[#2563EB] font-semibold text-2xl dark:bg-[rgba(37,99,235,.18)] dark:text-[#60A5FA]">
              {contact.name.charAt(0).toUpperCase()}
            </div>
          )}
          <p className="text-2xl text-[#0F172A] font-bold text-center mt-3 dark:text-white">{contact.name}</p>
          <p className="text-sm font-normal text-[#64748B] mt-1 dark:text-[#A1A1AA]">
            {t("common:shared.createdOn", { date: formatDateInAccountTz(contact.created_at) })}
          </p>
        </div>
        <div className="w-full mt-6 flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <p className={labelClass}>{t("common.email")}</p>
            <p className={valueClass}>{contact.email || 'Não informado'}</p>
          </div>
          <div className="flex flex-col gap-1">
            <p className={labelClass}>{t("common.phone")}</p>
            <p className={valueClass}>{contact.phone || 'Não informado'}</p>
          </div>
          <div className="flex flex-col gap-1">
            <p className={labelClass}>{t("contactName.whatsappId")}</p>
            <p className={valueClass}>{contact.wa_id || 'Não informado'}</p>
          </div>
        </div>
        <div className="w-full mt-6 flex justify-center items-center">
          <Link to={`/chat/${contact.id}`} className="w-full">
            <Button className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white transition-colors duration-200">
              {t("common.goToChat")}
            </Button>
          </Link>
        </div>
      </div>

      {/* Custom fields ----------------------------> */}
      <div className={`${cardClass} gap-3`}>
        <div className="w-full flex justify-between items-center">
          <h3 className="text-base font-semibold text-[#0F172A] dark:text-[#FAFAFA]">{t("common.customFields")}</h3>
          <button
            type="button"
            onClick={openEditDialog}
            className="text-[#64748B] hover:text-[#0F172A] transition-colors duration-200 dark:text-[#A1A1AA] dark:hover:text-[#FAFAFA]"
            aria-label={t("contactName.editCustomFields")}
          >
            <PlusCircle className="w-6 h-6" />
          </button>
        </div>
        <div className="w-full flex flex-col gap-3">
          {populatedCustomFields.length > 0 ? (
            populatedCustomFields.map(([key, value]) => {
              const fieldDef = customFieldDefs.find(f => f.name === key);
              return (
                <div key={key} className="flex flex-col gap-1">
                  <p className={labelClass}>{key}</p>
                  <p className={valueClass}>
                    {fieldDef?.field_type === 'date' && value
                      ? formatDateInAccountTz(value)
                      : String(value)}
                  </p>
                </div>
              );
            })
          ) : (
            <p className="text-sm text-[#94A3B8] dark:text-[#71717A]">{t("common.noCustomFields")}</p>
          )}
        </div>
      </div>

      <Dialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          if (open) syncEditForm();
          setEditDialogOpen(open);
        }}
      >
        <DialogContent className="flex max-h-[85vh] w-full max-w-md flex-col gap-0 overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white p-0 text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA]">
          <DialogHeader className="shrink-0 border-b border-[#E5E7EB] px-6 py-4 dark:border-[#3F3F46]">
            <DialogTitle className="text-[#0F172A] dark:text-[#FAFAFA]">{t("contactName.customFieldsTitle")}</DialogTitle>
          </DialogHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4">
            {customFieldsLoading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-[#64748B] dark:text-[#A1A1AA]">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("contactName.loadingFields")}
              </div>
            ) : activeCustomFields.length > 0 ? (
              activeCustomFields.map((field) => (
                <div key={field.id} className="flex flex-col gap-2">
                  <Label htmlFor={`cf-detail-${field.id}`} className="text-[#0F172A] dark:text-[#FAFAFA]">
                    {field.name}{field.is_required ? ' *' : ''}
                  </Label>
                  {field.field_type === 'text' && (
                    <Input
                      id={`cf-detail-${field.id}`}
                      placeholder={field.name}
                      value={editCustomFields[field.name] || ''}
                      onChange={(e) => setEditCustomFields(prev => ({ ...prev, [field.name]: e.target.value }))}
                      className={inputClass}
                    />
                  )}
                  {field.field_type === 'number' && (
                    <Input
                      id={`cf-detail-${field.id}`}
                      type="number"
                      placeholder={field.name}
                      value={editCustomFields[field.name] || ''}
                      onChange={(e) => setEditCustomFields(prev => ({ ...prev, [field.name]: e.target.value }))}
                      className={inputClass}
                    />
                  )}
                  {field.field_type === 'date' && (
                    <Input
                      id={`cf-detail-${field.id}`}
                      type="date"
                      value={editCustomFields[field.name] || ''}
                      onChange={(e) => setEditCustomFields(prev => ({ ...prev, [field.name]: e.target.value }))}
                      className={`${inputClass} [color-scheme:light] dark:[color-scheme:dark]`}
                    />
                  )}
                  {(field.field_type === 'select' || field.field_type === 'multi_select') && (
                    <Select
                      value={editCustomFields[field.name] || ''}
                      onValueChange={(value) => setEditCustomFields(prev => ({ ...prev, [field.name]: value === '__none__' ? '' : value }))}
                    >
                      <SelectTrigger className={`w-full ${inputClass}`}>
                        <SelectValue placeholder={`Selecione ${field.name}`} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="__none__">{t("common.none")}</SelectItem>
                          {(field.options || []).map((option: string) => (
                            <SelectItem key={option} value={option}>{option}</SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              ))
            ) : (
              <p className="text-sm text-[#94A3B8] dark:text-[#71717A]">
                {t("contactName.noneConfigured")}
              </p>
            )}
          </div>
          <div className="shrink-0 border-t border-[#E5E7EB] px-6 py-4 dark:border-[#3F3F46]">
            <Button
              onClick={async () => {
                const success = await onUpdate({ custom_fields: editCustomFields });
                if (success) setEditDialogOpen(false);
              }}
              className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white transition-colors duration-200"
              disabled={customFieldsLoading || activeCustomFields.length === 0}
            >
              {t("common.save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Attributed Member ----------------------------> */}
      {attributedMember && (
        <div className={`${cardClass} gap-3`}>
          <div className="w-full flex justify-between items-center">
            <h3 className="text-base font-semibold text-[#0F172A] dark:text-[#FAFAFA]">{t("common.owner")}</h3>
          </div>
          <div className="w-full flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#EEF4FF] flex items-center justify-center text-[#2563EB] font-semibold text-sm dark:bg-[rgba(37,99,235,.18)] dark:text-[#60A5FA]">
              {getInitials(attributedMember.name, attributedMember.email)}
            </div>
            <div className="flex flex-col">
              <p className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
                {attributedMember.name || attributedMember.email}
              </p>
              {attributedMember.name && (
                <p className="text-xs text-[#64748B] dark:text-[#A1A1AA]">{attributedMember.email}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactName;
